// Left column of the Manufacturing → Plants screen (was the left-pane
// of the old two-pane shell; in slice 7 the panel collapses into a
// single column that stacks Plants list + persistent Unattached row +
// Dealers-attached box on top of each other, while a sister column on
// the right hosts the slice-5 Dealer locations slider).
//
// Renders the plants list with selection semantics + per-row "More"
// dropdown (Edit / Set Default / Deactivate / Delete). The list body
// is scroll-capped at ~3 rows so the column can dedicate vertical
// space to the dealers-attached affordances below; users with more
// plants scroll within the box instead of pushing other sections off
// the fold. The Unattached pseudo-row lives OUTSIDE the scroll area
// (rendered by ManufacturingPlantsColumn) so it stays visible
// regardless of scroll position.
//
// Slice 7 also moved Edit / Set Default / Deactivate / Delete behind
// a per-row "More ▾" dropdown (PlantRowActionsMenu) so the row's
// primary surface stays clean for the selection chrome + dealer
// count chips. The delete-confirm modal is owned by the column.
import { InlineHelp } from "@/components/common/InlineHelp";
import { PlantDeliveryAddressField } from "@/components/manufacturing/PlantDeliveryAddressField";
import { SectionCard } from "@/components/common/SectionCard";
import {
  ghostBtnClass,
  inputClass,
  mfgCompactGhostBtnClass,
  mfgCompactPrimaryBtnClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { useManufacturingPlantsTab } from "./useManufacturingPlantsTab";
import { UNATTACHED_PLANT_SENTINEL, type PlantSelection } from "./plantDealersDrafts";
import type {
  DealerRosterCounts,
  SubscriberLocationPlantCounts,
} from "./plantDealersFilter";
import { PlantRowActionsMenu } from "./PlantRowActionsMenu";
import type { MfgPlant } from "@/lib/manufacturingClient";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";

// Slice 8: compact button variants used inside the SectionCard
// `rightSlot` so the +New plant / Refresh affordances don't compete
// vertically with the help (`?`) button. The panel-level button bar
// was retired in the same slice so these are now the only entry
// points for both actions.
function PlantsHelp() {
  return (
    <InlineHelp label="What are plants?" placement="left">
      <p className="font-medium text-gray-800 dark:text-zinc-100">Plants</p>
      <p className="mt-1">
        Per-org production sites. Each plant has its own materials catalog and
        WAC, so the same SKU at two plants tracks two separate cost histories.
      </p>
      <p className="mt-1">
        Every org has a default plant — that's where new materials land unless
        you pick another. The default cannot be deactivated; promote a
        different plant first.
      </p>
      <p className="mt-1">
        The auto-seeded <code>DEFAULT</code> placeholder can be renamed in
        place to your real plant code (e.g. <code>CHEROKEE-PRIMARY</code>) —
        existing materials and units stay linked through the rename.
      </p>
      <p className="mt-1">
        Select a plant to manage its attached dealers in the pane to the
        right.
      </p>
    </InlineHelp>
  );
}

export function ManufacturingPlantsList({
  m,
  selection,
  onSelect,
  rosterCounts,
  subscriberLocationCounts,
  emptyState,
  onRequestDelete,
  onRequestNewPlant,
  onRequestRefresh,
  refreshBusy = false,
  gateEnabled = true,
  gateLoading = false,
}: {
  m: ReturnType<typeof useManufacturingPlantsTab>;
  selection: PlantSelection;
  onSelect: (next: PlantSelection) => void;
  rosterCounts: DealerRosterCounts;
  /** Slice 6: per-plant effective-plant counts for app-using
   *  subscriber dealer locations (public.locations overlay).
   *  Optional so legacy callers still typecheck; the panel passes
   *  this in from useManufacturingPlantSubscriberLocationCounts. */
  subscriberLocationCounts?: SubscriberLocationPlantCounts;
  emptyState?: React.ReactNode;
  /** Slice 7: per-row Delete action opens a confirm modal owned by
   *  the parent column (so the list stays presentational). */
  onRequestDelete?: (plant: MfgPlant) => void;
  /** Slice 8: + New plant / Refresh affordances live in the
   *  SectionCard `rightSlot` next to the help (`?`) button now.
   *  Callbacks are owned by the panel so the gate-refresh side
   *  effect (used by both buttons) stays in one place. */
  onRequestNewPlant?: () => void;
  onRequestRefresh?: () => void;
  refreshBusy?: boolean;
  gateEnabled?: boolean;
  gateLoading?: boolean;
}) {
  const headerActions = (
    <div className="flex items-center gap-2">
      {onRequestNewPlant ? (
        <button
          type="button"
          className={mfgCompactPrimaryBtnClass}
          onClick={onRequestNewPlant}
          disabled={!gateEnabled || gateLoading}
          title="Create a new plant for this org"
        >
          + New plant
        </button>
      ) : null}
      {onRequestRefresh ? (
        <button
          type="button"
          className={mfgCompactGhostBtnClass}
          onClick={onRequestRefresh}
          disabled={gateLoading || refreshBusy}
          title="Reload plants + dealers from the server"
        >
          {refreshBusy ? "Refreshing…" : "Refresh"}
        </button>
      ) : null}
      <PlantsHelp />
    </div>
  );

  return (
    <div {...{ [FIREFLY_TARGET_ATTR]: "mfg-plants-list" }}>
      <SectionCard title="Plants" rightSlot={headerActions}>
      {m.creating ? (
        <div className="space-y-2 mb-3 rounded-2xl border border-indigo-200 bg-indigo-50/30 p-3 dark:border-indigo-800 dark:bg-indigo-950/15">
          <label className="block text-xs text-gray-600 dark:text-zinc-300">
            Plant code
            <input
              className={`${inputClass} mt-1`}
              value={m.newCode}
              onChange={(e) => m.setNewCode(e.target.value)}
              placeholder="e.g. MAIN, PLANT2, EAST"
            />
          </label>
          <label className="block text-xs text-gray-600 dark:text-zinc-300">
            Name
            <input
              className={`${inputClass} mt-1`}
              value={m.newName}
              onChange={(e) => m.setNewName(e.target.value)}
              placeholder="e.g. Main plant"
            />
          </label>
          <label className="block text-xs text-gray-600 dark:text-zinc-300">
            Delivery address
            <div className="mt-1">
              <PlantDeliveryAddressField
                value={m.newDeliveryAddress}
                onChange={m.setNewDeliveryAddress}
                disabled={m.actionBusy}
              />
            </div>
          </label>
          <label className="block text-xs text-gray-600 dark:text-zinc-300">
            Notes
            <input
              className={`${inputClass} mt-1`}
              value={m.newNotes}
              onChange={(e) => m.setNewNotes(e.target.value)}
            />
          </label>
          <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-zinc-300">
            <input
              type="checkbox"
              checked={m.newIsDefault}
              onChange={(e) => m.setNewIsDefault(e.target.checked)}
            />
            Make this the default plant for the org
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              className={mfgPrimaryBtnClass}
              onClick={() => void m.submitCreate()}
              disabled={m.actionBusy}
            >
              {m.actionBusy ? "Saving…" : "Create plant"}
            </button>
            <button type="button" className={ghostBtnClass} onClick={m.cancelCreate}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {m.listBusy ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">Loading plants…</div>
      ) : null}
      {!m.listBusy && m.plants.length === 0 ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">
          No plants yet. Use + New plant — or re-apply the slice 4a DDL block to auto-seed a default.
        </div>
      ) : null}

      {/* Slice 9 sizing:
          - min-h-[14rem] guarantees ~3 plant rows of vertical space
            even when the org has a single plant, so the SectionCard
            doesn't visually collapse around its lone row.
          - max-h-[32rem] (~7 rows, +18% over slice 8) keeps the
            scroll cap generous; multi-plant orgs still scroll.
          The scroll body lives in its own div so the empty-state
          and loading messages above it don't fight with the ul's
          min-height. */}
      <ul className="min-h-[14rem] max-h-[32rem] overflow-y-auto overflow-x-hidden divide-y divide-gray-100 dark:divide-zinc-700">
        {m.plants.map((p) => {
          const isEditing = m.editingId === p.id;
          const isSelected = selection === p.id;
          const attachedCount = rosterCounts.byPlantId.get(p.id) ?? 0;
          const subLocCount = subscriberLocationCounts?.byPlantId.get(p.id) ?? 0;
          return (
            <li
              key={p.id}
              // Slice 9 (chrome) + slice 10 (ring-inset). The
              // selected-row ring now draws INSIDE the row's
              // border-box so it survives the parent ul's
              // overflow-x-hidden — slice 9's outset ring was being
              // clipped on the right edge, showing as a "partial
              // blue outline". ring-inset side-steps the box-shadow
              // clipping entirely without adding layout-shifting
              // borders or extra DOM.
              className={`px-1 py-3 cursor-pointer ${
                isSelected
                  ? "rounded-lg ring-1 ring-inset ring-indigo-300 dark:ring-indigo-700"
                  : "hover:bg-gray-50 dark:hover:bg-zinc-800/40"
              }`}
              // Slice 9: clicking the row that's already selected
              // toggles to the Unattached pseudo-state (sentinel)
              // rather than null. This lets the dealers-attached
              // slider auto-switch to its Unattached tab (per the
              // panel's selection↔mode sync) instead of the user
              // landing in a "nothing here" empty state.
              onClick={() => {
                if (isEditing) return;
                onSelect(isSelected ? UNATTACHED_PLANT_SENTINEL : p.id);
              }}
              aria-pressed={isSelected}
              title={isSelected ? "Click again to view unattached dealers" : `Select ${p.plant_code}`}
            >
              {isEditing ? (
                <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                  <label className="block text-xs text-gray-600 dark:text-zinc-300">
                    Plant code
                    <input
                      className={`${inputClass} mt-1`}
                      value={m.edit.plant_code}
                      onChange={(e) => m.setEdit({ ...m.edit, plant_code: e.target.value })}
                      placeholder="e.g. MAIN, CHEROKEE-PRIMARY"
                    />
                  </label>
                  <input
                    className={inputClass}
                    value={m.edit.name}
                    onChange={(e) => m.setEdit({ ...m.edit, name: e.target.value })}
                    placeholder="Name"
                  />
                  <PlantDeliveryAddressField
                    value={m.edit.delivery_address}
                    onChange={(delivery_address) => m.setEdit({ ...m.edit, delivery_address })}
                    disabled={m.actionBusy}
                  />
                  <input
                    className={inputClass}
                    value={m.edit.notes}
                    onChange={(e) => m.setEdit({ ...m.edit, notes: e.target.value })}
                    placeholder="Notes"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className={mfgPrimaryBtnClass}
                      onClick={() => void m.submitEdit()}
                      disabled={m.actionBusy}
                    >
                      {m.actionBusy ? "Saving…" : "Save"}
                    </button>
                    <button type="button" className={ghostBtnClass} onClick={m.cancelEdit}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0 space-y-1">
                    {/* Slice 9: name + badges line. `min-w-0 truncate`
                        on the inner span keeps long plant names from
                        forcing horizontal overflow; the badges sit
                        beside it as inline-flex children that wrap
                        below the name when the row gets narrow. */}
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-medium text-gray-900 dark:text-zinc-100">
                      <span className="min-w-0 truncate">
                        <code>{p.plant_code}</code>
                        <span className="text-gray-600 dark:text-zinc-400"> · </span>
                        {p.name}
                      </span>
                      {p.is_default ? (
                        <span className="shrink-0 rounded-md bg-indigo-600 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                          Default
                        </span>
                      ) : null}
                      {!p.is_active ? (
                        <span className="shrink-0 rounded-md bg-gray-400 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                          Inactive
                        </span>
                      ) : null}
                    </div>
                    {p.notes ? (
                      <div className="text-xs text-gray-600 dark:text-zinc-400 truncate">
                        {p.notes}
                      </div>
                    ) : null}
                    {/* Slice 9: count chips moved off the same line as
                        the name + badges so a long plant name can't
                        push the chips into horizontal scroll. */}
                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-zinc-400">
                      <span title="Subscriber dealer orgs + non-app locations whose primary plant is this plant">
                        ▸ {attachedCount} dealer{attachedCount === 1 ? "" : "s"}
                      </span>
                      {subLocCount > 0 ? (
                        <span
                          className="rounded-md bg-emerald-100 px-1.5 py-0.5 font-semibold text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-100"
                          title="App-using dealer locations whose effective plant resolves to this plant (direct overlay → subscriber-org default → your default plant)"
                        >
                          📍 {subLocCount} sub-loc
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="shrink-0">
                    <PlantRowActionsMenu
                      plant={p}
                      busy={m.actionBusy}
                      onEdit={(plant) => m.startEdit(plant)}
                      onSetDefault={(plant) => void m.promoteToDefault(plant.id)}
                      onToggleActive={(plant) => void m.toggleActive(plant)}
                      onDelete={(plant) => onRequestDelete?.(plant)}
                    />
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {emptyState}
    </SectionCard>
    </div>
  );
}
